'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import Icon from '@/app/rupiqa/website-components/ui/AppIcon';
import { Eye, EyeOff, LockKeyhole } from 'lucide-react';

export default function LoginForm({
  embedded = false,
  homepage = false,
}: { embedded?: boolean; homepage?: boolean } = {}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [mustChangeInternalPassword, setMustChangeInternalPassword] = useState(false);
  const [passwordMode, setPasswordMode] = useState('internal');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const inputClass =
    'w-full px-3 py-3 bg-white border border-slate-300 rounded-lg text-base text-slate-950 placeholder:text-slate-400 focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-50';

  const loginError = (value: unknown) => {
    const text = typeof value === 'string' ? value.trim() : '';
    if (!text || text === '{}' || text === '[object Object]') return 'Login details check karein.';
    if (/invalid|credential|password|email|login|sign in/i.test(text))
      return 'Login details check karein.';
    if (/profile|active|missing/i.test(text))
      return 'Account access is not active. Admin se access check karwayein.';
    return text;
  };

  useEffect(() => {
    try {
      const hint = JSON.parse(sessionStorage.getItem('rupiqa.partnerLoginHint') || 'null');
      sessionStorage.removeItem('rupiqa.partnerLoginHint');
      if (
        hint &&
        typeof hint.email === 'string' &&
        Date.now() - hint.at < 5 * 60 * 1000 &&
        Date.now() >= hint.at
      ) {
        setEmail(hint.email);
        setMessage('Password set. Sign in to continue.');
      }
    } catch {
      /* Prefill is optional. */
    }
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && window.parent !== window)
        window.parent.postMessage({ type: 'rupiqa:close-login' }, window.location.origin);
    };
    window.addEventListener('keydown', closeOnEscape);
    const params = new URLSearchParams(window.location.search);
    const error = params.get('error');
    if (error) setMessage(error);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, []);

  useEffect(() => {
    if (!homepage || window.parent === window) return;
    const content = document.querySelector('main') || document.body;
    const reportHeight = () =>
      window.parent.postMessage(
        { type: 'rupiqa:login-height', height: content.scrollHeight },
        window.location.origin
      );
    const observer = new ResizeObserver(reportHeight);
    observer.observe(content);
    reportHeight();
    return () => observer.disconnect();
  }, [homepage]);

  const finishLogin = async (loginPassword: string, loginEmail = email.trim()) => {
    const response = await fetch('/api/auth/employee-login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: loginEmail, password: loginPassword }),
    });
    const payload = await response.json().catch(() => ({}));

    if (payload.mustChangePassword) {
      setPasswordMode(payload.passwordMode || 'internal');
      setMustChangeInternalPassword(true);
      throw new Error(payload.error || 'Create a new password to continue.');
    }

    if (!response.ok || !payload.ok) {
      throw new Error(loginError(payload.error));
    }

    window.localStorage.removeItem('rupiqa.activeUserId');
    if (payload.userId) window.localStorage.setItem('rupiqa.activeUserId', payload.userId);

    // Route to authenticated dashboard — never back to public homepage
    const route = payload.route || '/rupiqa/admin';
    // If route is /rupiqa (public homepage), redirect to admin dashboard instead
    const safeRoute = route === '/rupiqa' ? '/rupiqa/admin' : route;
    (window.top || window).location.assign(safeRoute);
  };

  const handleSignIn = async (event: React.FormEvent) => {
    event.preventDefault();
    if (loading) return;
    setMessage('');

    const fields = new FormData(event.currentTarget as HTMLFormElement);
    const submittedEmail = String(fields.get('email') || '').trim();
    const submittedPassword = String(fields.get('password') || '');
    if (!submittedEmail || !submittedPassword) {
      setMessage('Enter email and password to continue.');
      return;
    }

    setLoading(true);
    try {
      setEmail(submittedEmail);
      setPassword(submittedPassword);
      await finishLogin(submittedPassword, submittedEmail);
    } catch (error) {
      setMessage(loginError(error instanceof Error ? error.message : null));
      setLoading(false);
    }
  };

  const handleInternalPasswordUpdate = async (event: React.FormEvent) => {
    event.preventDefault();
    setMessage('');

    if (newPassword.length < (passwordMode === 'calling' ? 12 : 8)) {
      setMessage(
        `New password must be at least ${passwordMode === 'calling' ? 12 : 8} characters.`
      );
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage('New password and confirm password do not match.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        passwordMode === 'calling'
          ? '/api/auth/calling-password'
          : passwordMode === 'partner'
            ? '/api/auth/partner-password'
            : '/api/auth/internal-super-admin-password',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: email.trim(),
            currentPassword: password,
            newPassword,
          }),
        }
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.ok) {
        setMessage(payload.error || 'Password could not be updated.');
        setLoading(false);
        return;
      }

      await finishLogin(newPassword);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Password could not be updated.');
      setLoading(false);
    }
  };

  return (
    <div className="w-full">
      <div
        className={
          embedded
            ? 'bg-white'
            : 'rounded-2xl border border-slate-200/80 bg-white overflow-hidden shadow-[0_8px_40px_-16px_rgba(15,23,42,0.16)]'
        }
      >
        <div className={homepage ? 'px-6 pt-8 text-center sm:px-9' : 'px-6 pt-7 sm:px-8'}>
          {homepage && (
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
              <LockKeyhole size={24} aria-hidden="true" />
            </div>
          )}
          <h1 className="font-semibold text-2xl tracking-tight text-slate-950">
            {mustChangeInternalPassword ? 'Set new password' : 'Welcome back'}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {homepage ? 'Sign in to your Rupiqa workspace' : 'Sign in to RUPIQA.'}
          </p>
        </div>

        <div className="px-6 pb-7 pt-6 sm:px-8">
          <form
            onSubmit={mustChangeInternalPassword ? handleInternalPasswordUpdate : handleSignIn}
            className="space-y-5"
            aria-busy={loading}
          >
            <div>
              <label
                htmlFor="portal-email"
                className="block text-sm font-medium text-slate-700 mb-1.5"
              >
                Email
              </label>
              <input
                type="email"
                name="email"
                id="portal-email"
                autoComplete="username"
                required
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  setMustChangeInternalPassword(false);
                  setMessage('');
                }}
                placeholder="name@company.com"
                disabled={mustChangeInternalPassword}
                style={homepage ? { borderRadius: 10 } : undefined}
                className={inputClass}
              />
            </div>

            <div>
              <label
                htmlFor="portal-password"
                className="block text-sm font-medium text-slate-700 mb-1.5"
              >
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  id="portal-password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(event) => {
                    setPassword(event.target.value);
                    setMustChangeInternalPassword(false);
                    setMessage('');
                  }}
                  aria-describedby={message ? 'login-message' : undefined}
                  placeholder={
                    mustChangeInternalPassword ? 'Current temporary password' : 'Enter password'
                  }
                  disabled={mustChangeInternalPassword}
                  style={homepage ? { borderRadius: 10 } : undefined}
                  className={`${inputClass} pr-12`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((value) => !value)}
                  disabled={loading || mustChangeInternalPassword}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                  className="absolute inset-y-0 right-0 flex w-12 items-center justify-center rounded-r-lg text-slate-500 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 disabled:opacity-40"
                >
                  {showPassword ? (
                    <EyeOff size={18} aria-hidden="true" />
                  ) : (
                    <Eye size={18} aria-hidden="true" />
                  )}
                </button>
              </div>
              {!mustChangeInternalPassword && (
                <Link
                  target="_top"
                  href="/rupiqa/partner/reset"
                  className="mt-2 inline-flex min-h-9 items-center text-sm font-medium text-blue-700 hover:underline"
                >
                  Forgot password?
                </Link>
              )}
            </div>

            {mustChangeInternalPassword && (
              <>
                <div className="text-sm text-slate-600">
                  First login detected. Set a new password once and continue.
                </div>
                <div>
                  <label
                    htmlFor="portal-new-password"
                    className="block text-sm font-medium text-slate-700 mb-1.5"
                  >
                    New Password
                  </label>
                  <input
                    type="password"
                    id="portal-new-password"
                    autoComplete="new-password"
                    required
                    minLength={passwordMode === 'calling' ? 12 : 8}
                    value={newPassword}
                    onChange={(event) => setNewPassword(event.target.value)}
                    placeholder="Create new password"
                    style={homepage ? { borderRadius: 10 } : undefined}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label
                    htmlFor="portal-confirm-password"
                    className="block text-sm font-medium text-slate-700 mb-1.5"
                  >
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    id="portal-confirm-password"
                    autoComplete="new-password"
                    required
                    minLength={passwordMode === 'calling' ? 12 : 8}
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    placeholder="Confirm new password"
                    style={homepage ? { borderRadius: 10 } : undefined}
                    className={inputClass}
                  />
                </div>
              </>
            )}

            {message && (
              <div
                id="login-message"
                role="alert"
                className="rounded-lg bg-amber-50 px-3 py-2.5 text-sm text-amber-900"
              >
                {message}
              </div>
            )}

            <button
              type="submit"
              style={homepage ? { borderRadius: 10, minHeight: 48 } : undefined}
              disabled={
                loading || (mustChangeInternalPassword && (!newPassword || !confirmPassword))
              }
              className="flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Icon name="ArrowPathIcon" size={15} className="animate-spin" />
                  {mustChangeInternalPassword ? 'Updating password...' : 'Signing in...'}
                </>
              ) : (
                <>
                  {mustChangeInternalPassword
                    ? 'Update Password & Sign In'
                    : homepage
                      ? 'Login'
                      : 'Sign In'}
                  <Icon name="ArrowRightIcon" size={15} />
                </>
              )}
            </button>
          </form>

          {homepage ? (
            <p className="mt-5 flex items-center justify-center gap-2 text-xs text-slate-500">
              <LockKeyhole size={14} aria-hidden="true" />
              Secure access to your workspace
            </p>
          ) : (
            <p className="mt-6 border-t border-slate-100 pt-5 text-center text-sm text-slate-500">
              New to RUPIQA?{' '}
              <Link
                target="_top"
                href="/rupiqa/partner"
                className="text-blue-700 font-semibold hover:underline"
              >
                Become a partner
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
